import { useState, useEffect } from 'react';
import { Container, Title, Table, Badge, Loader, Alert, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import api from '@/config/api';

interface TaxForm {
  id: number;
  tax_year: number;
  form_type: string;
  status: string;
  filed_date: string | null;
}

export default function TaxForms() {
  const [forms, setForms] = useState<TaxForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get('/payroll/my/w2s')
      .then(res => setForms(res.data?.forms || []))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load tax forms'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader mx="auto" mt="xl" />;
  if (error) return <Alert color="red" title="Error">{error}</Alert>;

  return (
    <Container size="lg">
      <Title order={2} mb="lg">My Tax Forms</Title>
      {forms.length === 0 ? (
        <Alert color="blue">No tax forms available.</Alert>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tax Year</Table.Th>
              <Table.Th>Form Type</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Filed Date</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {forms.map(form => (
              <Table.Tr key={form.id}>
                <Table.Td>{form.tax_year}</Table.Td>
                <Table.Td>{form.form_type}</Table.Td>
                <Table.Td><Badge>{form.status}</Badge></Table.Td>
                <Table.Td>{form.filed_date || '-'}</Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => {
                      api.get(`/payroll/my/w2s/${form.id}/download`, { responseType: 'blob' })
                        .then((resp) => {
                          const url = window.URL.createObjectURL(new Blob([resp.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `${form.form_type}_${form.tax_year}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                        })
                        .catch(() => {
                          notifications.show({
                            title: 'Download Failed',
                            message: 'Could not download the tax form. Please try again.',
                            color: 'red',
                          });
                        });
                    }}
                  >
                    Download
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Container>
  );
}
